import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import { useUpsertBuyer } from 'src/hooks/api/useUpsertBuyer';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ApiException,
  BuyerUpsertDto,
  BuyerWithSalesDto,
  EventWithData,
  GenericAddressDto,
  ListingDetails,
  SaleClient,
  SaleLineItemCoverage,
} from 'src/WebApiController';

import {
  buildRetailSaleInput,
  getPaymentAmount,
  getPaymentCountryCode,
  getRetailMerchantReferenceId,
} from './RetailSaleLocalContext.utils';
import { RetailSaleFormValues } from './types';

export type AdyenSession = {
  saleId: number;
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  merchantReference: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type UseRetailSaleOperationsParams = {
  event: EventWithData | undefined;
  listing: ListingDetails | undefined;
  selectedBuyer: BuyerWithSalesDto | undefined;
  selectedStoredPaymentMethodId: string | null;
  setSelectedBuyer: (buyer: BuyerWithSalesDto) => void;
  isPartialPayment: boolean;
  partialAmount: number;
};

export const useRetailSaleOperations = ({
  event,
  listing,
  selectedBuyer,
  selectedStoredPaymentMethodId,
  setSelectedBuyer,
  isPartialPayment,
  partialAmount,
}: UseRetailSaleOperationsParams) => {
  const { getValues } = useFormContext<RetailSaleFormValues>();
  const { showErrorDialog, genericError, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const navigate = useNavigate();

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const upsertBuyer = useUpsertBuyer();
  const knownCountries = useKnownCountries();

  // Reference to track the created sale
  // In case of payment error, we want to avoid creating multiple sales for the same listing
  const saleIdRef = useRef<number | null>(null);

  const [adyenSession, setAdyenSession] = useState<AdyenSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [coverageDialogSaleId, setCoverageDialogSaleId] = useState<
    number | null
  >(null);

  const disableEditing = useMemo(() => {
    return isLoading || Boolean(adyenSession);
  }, [adyenSession, isLoading]);

  const onCompleteAsUnpaid = useCallback(() => {
    const values = getValues();
    setIsLoading(true);
    const saleInput = buildRetailSaleInput(
      values,
      listing,
      selectedBuyer,
      event
    );

    tryInvokeApi(
      async () => {
        const client = new SaleClient(activeAccountWebClientConfig);
        const result = await client.createRetailSaleForListingId(
          saleInput.listingId,
          saleInput
        );

        if (result.saleId) {
          navigate(getSaleDetailsRelativeUrl(result.saleId));
        } else {
          showErrorDialog(
            'SaleClient.createRetailSaleForListingId',
            {
              message: result.message ?? genericError,
              status: 500,
            } as ApiException,
            { trackErrorData: saleInput }
          );
        }
      },
      (error) => {
        showErrorDialog('SaleClient.createRetailSaleForListingId', error, {
          trackErrorData: saleInput,
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    event,
    genericError,
    getValues,
    listing,
    navigate,
    selectedBuyer,
    showErrorDialog,
  ]);

  const onPayAndComplete = useCallback(() => {
    const storedMethodId = selectedStoredPaymentMethodId;
    const values = getValues();
    setIsLoading(true);

    tryInvokeApi(
      async () => {
        if (!saleIdRef.current) {
          const saleInput = buildRetailSaleInput(
            values,
            listing,
            selectedBuyer,
            event
          );
          const client = new SaleClient(activeAccountWebClientConfig);
          const placeholderResult = await client.createRetailSaleForListingId(
            saleInput.listingId,
            saleInput
          );

          if (!placeholderResult.saleId) {
            showErrorDialog(
              'SaleClient.createRetailSaleForListingId',
              {
                message: placeholderResult.message ?? genericError,
                status: 500,
              } as ApiException,
              { trackErrorData: saleInput }
            );
            return;
          }

          saleIdRef.current = placeholderResult.saleId;
        }

        // If partial payment, open the coverage dialog and let user assign amounts
        if (isPartialPayment) {
          setCoverageDialogSaleId(saleIdRef.current);
          return; // tryInvokeApi's finally block sets isLoading = false
        }

        const amount = getPaymentAmount(values);
        const merchantReference = getRetailMerchantReferenceId(
          saleIdRef.current
        );

        try {
          if (storedMethodId) {
            await submitPaymentWithStoredMethod.mutateAsync({
              saleId: saleIdRef.current,
              storedPaymentMethodId: storedMethodId,
              amount,
              currencyCode: values.currencyCode ?? 'USD',
              buyerEmail: values.buyerEmail ?? null,
              reference: merchantReference,
              lineItemCoverages: null,
            });
            navigate(getSaleDetailsRelativeUrl(saleIdRef.current));
          } else {
            const countryCode = getPaymentCountryCode(
              selectedBuyer,
              knownCountries.data
            );
            const sessionResponse = await createPaymentSession.mutateAsync({
              saleId: saleIdRef.current,
              amount,
              currencyCode: values.currencyCode ?? 'USD',
              buyerEmail: values.buyerEmail ?? null,
              returnUrl: window.location.href,
              reference: merchantReference,
              countryCode,
              lineItemCoverages: null,
            });

            setAdyenSession({
              saleId: saleIdRef.current,
              sessionId: sessionResponse.sessionId,
              sessionData: sessionResponse.sessionData,
              clientKey: sessionResponse.clientKey,
              environment: sessionResponse.environment,
              merchantReference,
              countryCode,
              lineItemCoverages: null,
            });
          }
        } catch (error: any) {
          trackError('AdyenPayment.onError', new Error(error.message));
          navigate(getSaleDetailsRelativeUrl(saleIdRef.current));
        }
      },
      (error) => {
        showErrorDialog('RetailSale.onPayAndComplete', error, {
          trackErrorData: { listingId: listing?.id ?? values.listingId },
        });
      },
      () => {
        setIsLoading(false);
      }
    );
  }, [
    activeAccountWebClientConfig,
    createPaymentSession,
    event,
    genericError,
    getValues,
    isPartialPayment,
    knownCountries.data,
    listing,
    navigate,
    selectedBuyer,
    selectedStoredPaymentMethodId,
    showErrorDialog,
    submitPaymentWithStoredMethod,
    trackError,
  ]);

  const onAdyenPaymentCompleted = useCallback(
    async (result: AdyenPaymentCompletedResult) => {
      if (!adyenSession) return;

      if (result.resultCode !== 'Authorised') {
        setIsLoading(false);
        setAdyenSession(null);
        showErrorDialog(
          'RetailSale.onPayAndComplete',
          {
            message: `Payment not authorized. Result code: ${result.resultCode}`,
          },
          {
            trackErrorData: {
              resultCode: result.resultCode,
              saleId: adyenSession.saleId,
            },
          }
        );
        return;
      }

      tryInvokeApi(
        async () => {
          await submitPaymentResult.mutateAsync({
            saleId: adyenSession.saleId,
            input: {
              resultCode: result.resultCode,
              sessionResult: result.sessionResult ?? null,
              merchantReference: adyenSession.merchantReference,
              pspReference: null,
              lineItemCoverages: adyenSession.lineItemCoverages,
            },
          });

          navigate(getSaleDetailsRelativeUrl(adyenSession.saleId));
        },
        (error) => {
          showErrorDialog('RetailSale.onPayAndComplete', error, {
            trackErrorData: {
              listingId: listing?.id,
              saleId: adyenSession.saleId,
            },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      adyenSession,
      listing?.id,
      navigate,
      setIsLoading,
      showErrorDialog,
      submitPaymentResult,
    ]
  );

  const onAdyenError = useCallback(
    (error: { message: string }) => {
      trackError('AdyenDropIn.onError', new Error(error.message));
      setAdyenSession(null);
    },
    [setAdyenSession, trackError]
  );

  const onCoverageConfirmed = useCallback(
    (coverages: SaleLineItemCoverage[]) => {
      setCoverageDialogSaleId(null);
      const storedMethodId = selectedStoredPaymentMethodId;
      const values = getValues();
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          const merchantReference = getRetailMerchantReferenceId(
            saleIdRef.current!
          );
          try {
            if (storedMethodId) {
              await submitPaymentWithStoredMethod.mutateAsync({
                saleId: saleIdRef.current!,
                storedPaymentMethodId: storedMethodId,
                amount: partialAmount,
                currencyCode: values.currencyCode ?? 'USD',
                buyerEmail: values.buyerEmail ?? null,
                reference: merchantReference,
                lineItemCoverages: coverages,
              });
              navigate(getSaleDetailsRelativeUrl(saleIdRef.current!));
            } else {
              const countryCode = getPaymentCountryCode(
                selectedBuyer,
                knownCountries.data
              );
              const sessionResponse = await createPaymentSession.mutateAsync({
                saleId: saleIdRef.current!,
                amount: partialAmount,
                currencyCode: values.currencyCode ?? 'USD',
                buyerEmail: values.buyerEmail ?? null,
                returnUrl: window.location.href,
                reference: merchantReference,
                countryCode,
                lineItemCoverages: coverages,
              });
              setAdyenSession({
                saleId: saleIdRef.current!,
                sessionId: sessionResponse.sessionId,
                sessionData: sessionResponse.sessionData,
                clientKey: sessionResponse.clientKey,
                environment: sessionResponse.environment,
                merchantReference,
                countryCode,
                lineItemCoverages: coverages,
              });
            }
          } catch (error: any) {
            trackError('AdyenPayment.onError', new Error(error.message));
            navigate(getSaleDetailsRelativeUrl(saleIdRef.current!));
          }
        },
        (error) => {
          showErrorDialog('RetailSale.onCoverageConfirmed', error, {
            trackErrorData: { saleId: saleIdRef.current },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      createPaymentSession,
      getValues,
      knownCountries.data,
      navigate,
      partialAmount,
      selectedBuyer,
      selectedStoredPaymentMethodId,
      setAdyenSession,
      showErrorDialog,
      submitPaymentWithStoredMethod,
      trackError,
    ]
  );

  const onCoverageDialogCancel = useCallback(() => {
    setCoverageDialogSaleId(null);
  }, []);

  const onUpsertBuyer = useCallback(async (): Promise<boolean> => {
    const values = getValues();

    const needsUpsert =
      !selectedBuyer ||
      !selectedBuyer.shippingAddressLine1 ||
      !selectedBuyer.shippingCity ||
      !selectedBuyer.shippingState ||
      !selectedBuyer.shippingZipCode ||
      !selectedBuyer.shippingCountry ||
      !selectedBuyer.billingAddressLine1 ||
      !selectedBuyer.billingCity ||
      !selectedBuyer.billingState ||
      !selectedBuyer.billingZipCode ||
      !selectedBuyer.billingCountry;

    if (!needsUpsert) return true;

    const buildAddress = (
      street: string | undefined,
      suite: string | undefined,
      city: string | undefined,
      state: string | undefined,
      zipCode: string | undefined,
      country: string | undefined
    ): GenericAddressDto | null => {
      if (!street && !state && !zipCode && !country) return null;
      return {
        genericAddressId: 0,
        addressLine1: street ?? null,
        addressLine2: suite ?? null,
        city: city ?? null,
        state: state ?? null,
        zipCode: zipCode ?? null,
        country: country ?? null,
      };
    };

    const shippingAddress = buildAddress(
      values.shippingStreet,
      values.shippingSuite,
      values.shippingCity,
      values.shippingState,
      values.shippingZipCode,
      values.shippingCountry
    );
    const billingAddress = values.billingAddressSameAsShipping
      ? shippingAddress
      : buildAddress(
          values.billingStreet,
          values.billingSuite,
          values.billingCity,
          values.billingState,
          values.billingZipCode,
          values.billingCountry
        );

    const dto: BuyerUpsertDto = {
      buyerEmail: selectedBuyer?.buyerEmail ?? values.buyerEmail!,
      buyerName: selectedBuyer?.buyerName ?? values.buyerName ?? null,
      buyerPhoneNumber:
        selectedBuyer?.buyerPhoneNumber ?? values.buyerPhone ?? null,
      internalNotes:
        selectedBuyer?.internalNotes ?? values.customerMessage ?? null,
      lastRetailActivityDate: null,
      shippingAddress,
      billingAddress,
    };

    setIsLoading(true);
    try {
      await upsertBuyer.mutateAsync(dto);

      setSelectedBuyer({
        ...(selectedBuyer ?? {}),
        buyerEmail: dto.buyerEmail,
        buyerName: dto.buyerName,
        buyerPhoneNumber: dto.buyerPhoneNumber,
        internalNotes: selectedBuyer?.internalNotes ?? null,
        lastRetailActivityDate: selectedBuyer?.lastRetailActivityDate ?? null,
        shippingAddressLine1: shippingAddress?.addressLine1 ?? null,
        shippingAddressLine2: shippingAddress?.addressLine2 ?? null,
        shippingCity: shippingAddress?.city ?? null,
        shippingState: shippingAddress?.state ?? null,
        shippingZipCode: shippingAddress?.zipCode ?? null,
        shippingCountry: shippingAddress?.country ?? null,
        billingAddressLine1: billingAddress?.addressLine1 ?? null,
        billingAddressLine2: billingAddress?.addressLine2 ?? null,
        billingCity: billingAddress?.city ?? null,
        billingState: billingAddress?.state ?? null,
        billingZipCode: billingAddress?.zipCode ?? null,
        billingCountry: billingAddress?.country ?? null,
        cardBrand: selectedBuyer?.cardBrand ?? null,
        cardSummary: selectedBuyer?.cardSummary ?? null,
        expiryMonth: selectedBuyer?.expiryMonth ?? null,
        expiryYear: selectedBuyer?.expiryYear ?? null,
        saleIds: selectedBuyer?.saleIds ?? null,
      });

      return true;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [getValues, selectedBuyer, setIsLoading, setSelectedBuyer, upsertBuyer]);

  return {
    disabled: disableEditing,
    adyenSession,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onPayAndComplete,
    onAdyenPaymentCompleted,
    onAdyenError,
    coverageDialogSaleId,
    onCoverageConfirmed,
    onCoverageDialogCancel,
  };
};
